import React, { useState, useEffect, useCallback } from 'react';
import { observabilityAPI } from '../services/api';
import './AlertSearchPage.css';

const SEVERITY_COLORS = { critical: 'var(--color-danger-500)', warning: 'var(--accent)', info: 'var(--brand)' };
const STATUS_COLORS   = { firing: 'var(--color-danger-500)', resolved: 'var(--color-success-500)' };

function TrendBar({ data, maxVal }) {
  return (
    <div className="trend-chart">
      {data.map((d, i) => {
        const h = maxVal > 0 ? Math.max(4, (d.count / maxVal) * 100) : 4;
        return (
          <div key={i} className="trend-bar-col" title={`${d.date}: ${d.count} alerts`}>
            <div className="trend-bar-fill" style={{ height: `${h}%` }} />
            <div className="trend-bar-date">{d.date.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function AlertSearchPage() {
  const [trends, setTrends]       = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [days, setDays]           = useState(7);

  const [query, setQuery]         = useState('');
  const [nsFilter, setNsFilter]   = useState('');
  const [sevFilter, setSevFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [results, setResults]     = useState(null);
  const [searching, setSearching] = useState(false);
  const [page, setPage]           = useState(1);

  const loadTrends = useCallback(async () => {
    setTrendsLoading(true);
    try {
      const res = await observabilityAPI.getAlertTrends(days);
      setTrends(res.data);
    } catch (e) {
      console.error('Failed to load trends', e);
    } finally {
      setTrendsLoading(false);
    }
  }, [days]);

  useEffect(() => { loadTrends(); }, [loadTrends]);

  const search = useCallback(async (p = 1) => {
    setSearching(true);
    try {
      const res = await observabilityAPI.searchAlerts({
        q: query, namespace: nsFilter || undefined,
        severity: sevFilter || undefined, status: statusFilter || undefined,
        days: 30, page: p, page_size: 20,
      });
      setResults(res.data);
      setPage(p);
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setSearching(false);
    }
  }, [query, nsFilter, sevFilter, statusFilter]);

  // Trigger search when filters change
  useEffect(() => {
    const t = setTimeout(() => search(1), 400);
    return () => clearTimeout(t);
  }, [search]);

  function timeAgo(iso) {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60)    return `${diff}s ago`;
    if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  }

  const maxDailyCount = trends ? Math.max(...(trends.daily || []).map(d => d.count), 1) : 1;

  return (
    <div className="alert-search-page resource-page">
      <div className="page-header">
        <h1>Alert Analytics & Search</h1>
        <select className="select" value={days} onChange={e => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {/* Trends section */}
      {!trendsLoading && trends && (
        <div className="as-trends-grid">
          {/* Daily chart */}
          <div className="as-card as-card-wide">
            <div className="as-card-title">Alerts per Day <span className="as-card-sub">({trends.total} total)</span></div>
            <TrendBar data={trends.daily || []} maxVal={maxDailyCount} />
          </div>

          {/* By severity */}
          <div className="as-card">
            <div className="as-card-title">By Severity</div>
            <div className="as-breakdown">
              {Object.entries(trends.by_severity || {}).sort((a,b) => b[1]-a[1]).map(([sev, count]) => (
                <div key={sev} className="as-breakdown-row">
                  <span className="as-breakdown-label" style={{ color: SEVERITY_COLORS[sev] || 'var(--muted)' }}>{sev}</span>
                  <div className="as-breakdown-bar-track">
                    <div className="as-breakdown-bar-fill"
                      style={{ width: `${(count / (trends.total || 1)) * 100}%`, background: SEVERITY_COLORS[sev] || 'var(--muted)' }} />
                  </div>
                  <span className="as-breakdown-count">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By status */}
          <div className="as-card">
            <div className="as-card-title">By Status</div>
            <div className="as-breakdown">
              {Object.entries(trends.by_status || {}).map(([st, count]) => (
                <div key={st} className="as-breakdown-row">
                  <span className="as-breakdown-label" style={{ color: STATUS_COLORS[st] || 'var(--muted)' }}>{st}</span>
                  <div className="as-breakdown-bar-track">
                    <div className="as-breakdown-bar-fill"
                      style={{ width: `${(count / (trends.total || 1)) * 100}%`, background: STATUS_COLORS[st] || 'var(--muted)' }} />
                  </div>
                  <span className="as-breakdown-count">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top alerts */}
          <div className="as-card">
            <div className="as-card-title">Top Alerts</div>
            <div className="as-top-list">
              {(trends.top_alerts || []).slice(0, 8).map((a, i) => (
                <div key={i} className="as-top-row">
                  <span className="as-top-rank">#{i+1}</span>
                  <span className="as-top-name" title={a.name}>{a.name.length > 32 ? a.name.slice(0,32)+'…' : a.name}</span>
                  <span className="as-top-count">{a.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top namespaces */}
          <div className="as-card">
            <div className="as-card-title">Top Namespaces</div>
            <div className="as-top-list">
              {(trends.top_namespaces || []).slice(0, 8).map((n, i) => (
                <div key={i} className="as-top-row">
                  <span className="as-top-rank">#{i+1}</span>
                  <span className="as-top-name" title={n.name}>{n.name || '(none)'}</span>
                  <span className="as-top-count">{n.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search section */}
      <div className="as-search-section">
        <div className="as-search-title">Search Alerts</div>
        <div className="as-search-bar">
          <input
            className="as-search-input"
            placeholder="Search alert name, namespace, pod, node, message..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <select value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="firing">Firing</option>
            <option value="resolved">Resolved</option>
          </select>
          <input placeholder="Namespace..." value={nsFilter} onChange={e => setNsFilter(e.target.value)}
            style={{ width: 140 }} />
        </div>

        {searching && <div style={{ padding: '16px 0', color: 'var(--muted)' }}>Searching...</div>}

        {results && !searching && (
          <>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
              {results.total} results found
            </div>
            <div className="resource-list">
              <table>
                <thead>
                  <tr>
                    <th>Alert</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Namespace</th>
                    <th>Pod</th>
                    <th>Age</th>
                  </tr>
                </thead>
                <tbody>
                  {results.alerts.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No alerts found</td></tr>
                  ) : results.alerts.map((a, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{a.alert_name}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          background: a.severity === 'critical' ? 'var(--color-danger-50)' : a.severity === 'warning' ? 'var(--color-warning-50)' : 'var(--brand-soft)',
                          color: SEVERITY_COLORS[a.severity] || 'var(--muted)' }}>
                          {a.severity}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          background: a.status === 'firing' ? 'var(--color-danger-50)' : 'var(--color-success-50)',
                          color: STATUS_COLORS[a.status] || 'var(--muted)' }}>
                          {a.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{a.namespace || '—'}</td>
                      <td style={{ fontSize: 12 }}><code>{a.pod_name || '—'}</code></td>
                      <td style={{ fontSize: 12, color: 'var(--muted-2)' }}>{timeAgo(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {results.pages > 1 && (
              <div className="as-pagination">
                <button disabled={page <= 1} onClick={() => search(page - 1)} className="btn btn-secondary btn-sm">← Prev</button>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Page {page} of {results.pages}</span>
                <button disabled={page >= results.pages} onClick={() => search(page + 1)} className="btn btn-secondary btn-sm">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
